import { ISort } from '../common/types';
import { AxiosResponse } from 'axios';
import { formatSortingFields } from '../common/helpers';
import { ApiSaleOrderService } from '../../services';
import { IPassengerAutocomplete } from '../salesOrder/types';
import {
	IPassengerDetail,
	IPassengerDetailApiResponse,
	IPassengerPage,
	IUpdatePassengerBillingSettingsPayload,
} from './types';

const normalizePassengerDetail = (data: IPassengerDetailApiResponse): IPassengerDetail => {
	const birthDate = data.birthDate ?? data.birthdate;
	const pendingPayAmount = data.pendingPayAmount;

	return {
		...data,
		birthDate,
		hasPendingPayAmount:
			data.hasPendingPayAmount ?? (pendingPayAmount !== undefined ? pendingPayAmount > 0 : undefined),
		saleOrdersRelated: data.saleOrdersRelated ?? [],
	};
};

const buildPassengersParams = (
	page: number,
	size: number,
	sortingFields: ISort[],
	filters?: Record<string, string | number | boolean>,
	search?: string,
): Record<string, string | number | boolean> => {
	const sorts = formatSortingFields(sortingFields);
	const params: Record<string, string | number | boolean> = {
		page,
		size,
		envelope: true,
	};

	if (sorts.length > 0) {
		params.sort = sorts;
	}

	if (search) {
		params.search = search;
	}

	if (filters) {
		Object.entries(filters).forEach(([key, value]) => {
			if (value === undefined || value === null || value === '') return;
			params[key] = value;
		});
	}

	return params;
};

export const getPassengersList = (
	page = 0,
	size = 10,
	sortingFields: ISort[],
	filters?: Record<string, string | number | boolean>,
	search?: string,
	signal?: AbortSignal,
): Promise<AxiosResponse<IPassengerPage>> =>
	ApiSaleOrderService.get('/passengers', {
		params: buildPassengersParams(page, size, sortingFields, filters, search),
		signal,
	});

export const getPassengersAutocomplete = (
	value: string,
	signal?: AbortSignal,
): Promise<AxiosResponse<IPassengerAutocomplete[]>> =>
	ApiSaleOrderService.get('/passengers/autocomplete', {
		params: { value },
		signal,
	});

export const getPassengerDetails = async (
	id: string,
	options?: { agencyId?: number },
): Promise<AxiosResponse<IPassengerDetail>> => {
	const response = await ApiSaleOrderService.get<IPassengerDetailApiResponse>(`passengers/detail/${id}`, {
		agencyId: options?.agencyId,
	});
	return { ...response, data: normalizePassengerDetail(response.data) };
};

export const updatePassengerBillingSettings = (
	passengerId: string,
	payload: IUpdatePassengerBillingSettingsPayload,
): Promise<void> =>
	ApiSaleOrderService.put(`passengers/billing-settings/${passengerId}`, payload).then(() => undefined);
